// ALL IMPORTS FIRST (MANDATORY)
import { useState, useEffect, useRef, useMemo } from "react";
import useSessionState from "../hooks/useSessionState";
import useRecentSearches from "../hooks/useRecentSearches";
import { Icon } from "../components/Icon";
import { useLocation, useNavigate } from "react-router-dom";
import { track } from "../utils/analytics";

import Header from "../components/Header";
import SearchBar from "../components/SearchBar";
import CarGrid, {
  matchesDriverAge, matchesCarType, matchesSeats, matchesYear, matchesFuelType,
  matchesExperience, matchesRating, matchesDriverTags, matchesBudgetRange, matchesLanguage,
} from "../components/CarGrid";
import FilterSheet from "../components/home/FilterSheet";
import BottomNav from "../components/BottomNav";
import { usePageMeta } from "../hooks/usePageMeta";
import FirstLaunchExplainer from "../components/FirstLaunchExplainer";
import LeadOutcomePrompt from "../components/LeadOutcomePrompt";
import {
  GuestCard, WhereCard, QuickChip, ChipRow, RecentEnquiryCard,
  useMyRecentLeads, recentEnquiryOf, hideEnquiry, tripWhen, shortPlace, placeText,
} from "../components/home/HomeAtoms";
import { TripIcons } from "../components/trip/TripFormAtoms";
import SeasonTrips from "../components/home/SeasonTrips";
import { DesktopHero, DesktopSearchBar, FilterPanel, DesktopFooter, TrustRow, toggled } from "../components/home/DesktopAtoms";
import { localCity } from "../utils/localNames";
import { intentDrop, parseQuery } from "../utils/queryParse";
import { SEASON_CITY } from "../data/seasonTrips";

import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { getAuth } from "../utils/auth";

import { calculateHours } from "../utils/dateUtils";
import { calculateFare } from "../shared/pricing/pricingEngine";
import { calculateTripMetrics } from "../utils/distanceUtils";
import { apiRequest } from "../utils/api";
import { fillText } from "../utils/leadDisplay";
import { canonicalCity } from "../utils/cities";
import { tripQuery, readTripQuery, syncTripUrl, shareTripLink } from "../utils/tripLink";
import { CallToBookBar, CallToBookLine, WantUsToBook } from "../components/home/CallToBook";
import {
  CAR_SORTS, CAR_TYPES, FUELS, YEARS, BUDGETS, RATINGS, EXPERIENCE, CAR_LANGS, TAGS, AGES,
  optionLabel, seatsValue, splitValues, customBudgetText,
} from "../utils/listChoices";

/* Sort options, in the order people actually reach for them on a phone. RECOMMENDED
   keeps whatever order the backend ranking returned, so the default is unchanged and
   the ranking engine stays the source of truth for "best". The names, and which sorts
   wait for a trip, are in utils/listChoices (CAR_SORTS). */

const STORAGE_KEY = "sramika_selected_city";

/* The From value the app itself used to write: a city's name and nothing else - no
   coordinates, which a place chosen in the picker always has. Recognised so it can be
   cleared; it was the centre of the city showing up as the customer's pickup. */
const isCityCentreFrom = (f) =>
  !!f && typeof f === "object" && !f.location && /^[^,]+, Andhra Pradesh, India$/.test(f.value || "");
/* Sramika operates in Vijayawada today, so that is where a first visit starts. The
   header showed "Select City" and an empty From until the visitor picked one - an
   extra step before any car appeared, for the answer nearly everyone gives. Anyone
   elsewhere changes it from the city picker as before. */
const DEFAULT_CITY = "Vijayawada";

/* The filters a customer can set, and the session keys they live under. A saved
   filter survives a trip to a car's page and back, as before. */
const FILTER_KEYS = ["seats", "carType", "fuelType", "year", "rating", "experience", "language", "driverTags", "driverAge", "budgetRange"];

export default function Home() {
  usePageMeta(
    "Sramika - Cabs & Hire Drivers in Vijayawada",
    "Find nearby cabs and hire drivers in Vijayawada. Compare real fares, then call the driver directly - no commission, no advance, no booking fee."
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const auth = getAuth();
  const { t, lang, setLang } = useLanguage();
  const locationAsked = useRef(false);
  const { recentSearches, addRecentSearch } = useRecentSearches();

  /* 🚕Trip type */
  const [tripType, setTripType] = useSessionState("home_tripType", "ROUND");

  /*  Date & time */
  const [fromDateTime, setFromDateTime] = useSessionState("home_fromDateTime", "");
  const [toDateTime, setToDateTime] = useSessionState("home_toDateTime", "");
  const [returnDateTime, setReturnDateTime] = useSessionState("home_returnDateTime", "");

  /*  Search and filters */
  const [searchQuery, setSearchQuery] = useSessionState("home_searchQuery", "");
  const [filterDriverAge, setFilterDriverAge] = useSessionState("home_filterDriverAge", "");
  const [filterCarType, setFilterCarType] = useSessionState("home_filterCarType", "");
  const [filterSeats, setFilterSeats] = useSessionState("home_filterSeats", "");
  const [filterYear, setFilterYear] = useSessionState("home_filterYear", "");
  const [filterFuelType, setFilterFuelType] = useSessionState("home_filterFuelType", "");
  const [filterExperience, setFilterExperience] = useSessionState("home_filterExperience", "");
  const [filterRating, setFilterRating] = useSessionState("home_filterRating", "");
  const [filterDriverTags, setFilterDriverTags] = useSessionState("home_filterDriverTags", "");
  const [filterBudgetRange, setFilterBudgetRange] = useSessionState("home_filterBudgetRange", "");
  const [filterLanguage, setFilterLanguage] = useSessionState("home_filterLanguage", "");

  /*  Locations */
  const [fromLocation, setFromLocation] = useSessionState("home_fromLocation", "");
  const [toLocation, setToLocation] = useSessionState("home_toLocation", "");
  // A city saved in another script before names were understood in every language is
  // read as the city it names ("విజయవాడ" -> Vijayawada), so the list is not empty.
  const [selectedCity, setSelectedCity] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return (saved && (canonicalCity(saved) || saved)) || DEFAULT_CITY;
  });
  const [stops, setStops] = useSessionState("home_stops", []);
  const [noCabsRequested, setNoCabsRequested] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [allCars, setAllCars] = useState([]);

  /* One place for every filter, so the chips, the sheet and "Clear" can never disagree
     about which filters exist. The music filter is gone: no car records one, so every
     car was "Bluetooth / Aux" and the filter could only ever hide cars. */
  const filters = {
    seats: [filterSeats, setFilterSeats],
    carType: [filterCarType, setFilterCarType],
    fuelType: [filterFuelType, setFilterFuelType],
    year: [filterYear, setFilterYear],
    rating: [filterRating, setFilterRating],
    experience: [filterExperience, setFilterExperience],
    language: [filterLanguage, setFilterLanguage],
    driverTags: [filterDriverTags, setFilterDriverTags],
    driverAge: [filterDriverAge, setFilterDriverAge],
    budgetRange: [filterBudgetRange, setFilterBudgetRange],
  };
  const filterValues = Object.fromEntries(FILTER_KEYS.map((k) => [k, filters[k][0]]));

  const clearAllFilters = () => FILTER_KEYS.forEach((k) => filters[k][1](""));

  // Counted, not just flagged: the number on Filters is what tells someone why the
  // list looks short.
  const activeFilterCount = FILTER_KEYS.filter((k) => filters[k][0]).length;

  // Sort is a separate question from filtering: filters say which cars qualify, sort
  // says which to look at first. Session-scoped so it survives a detail-page round
  // trip but never becomes a setting someone has to remember they changed.
  const [sortBy, setSortBy] = useSessionState("home_sortBy", "RECOMMENDED");
  // A sentence the search box understood is a request, not words to find in a car's
  // name - the list is left as it is until the request is used (mock Four_1 change 7).
  const listSearch = useMemo(() => (parseQuery(searchQuery) ? "" : searchQuery), [searchQuery]);

  /*  Derived values */
  const [hours, setHours] = useState(0);
  const [distanceKm, setDistanceKm] = useState(0);
  const [driveDurationMins, setDriveDurationMins] = useState(0);
  const [cityPricing, setCityPricing] = useState(null);

  /* Fetch admin-set city pricing whenever city changes */
  useEffect(() => {
    if (!selectedCity) { setCityPricing(null); return; }
    setCityPricing(null); // Clear old pricing while fetching
    // Using public endpoint to allow guest access to rates
    apiRequest(`/pricing?city=${encodeURIComponent(selectedCity)}`)
      .then(res => setCityPricing(res?.data?.pricing || res?.pricing || null))
      .catch(() => setCityPricing(null));
  }, [selectedCity]);

  /* First Launch Explainer (Section 9.1) */
  const [showExplainer, setShowExplainer] = useState(() => {
    return !localStorage.getItem("sramika_seen_explainer");
  });

  /* The explainer's last step asks what brought the visitor here and hands back where
     they want to go: an onboarding route for the two partner answers, nothing at all
     for "book a cab" and for Skip, which both mean stay on Home.

     Guarded on the type because the Skip Intro button is wired as onClick={onComplete}
     and so calls this with a click event. */
  function handleExplainerComplete(destination) {
    localStorage.setItem("sramika_seen_explainer", "true");
    setShowExplainer(false);
    if (typeof destination === "string" && destination.startsWith("/")) {
      navigate(destination);
    }
  }

  useEffect(() => {
    if (locationAsked.current) return;
    locationAsked.current = true;

    /* No "book the same trip again" lookup here any more. It read the retired booking
       system, which has had nothing new in it since enquiries replaced bookings, so it
       cost a request on every open and never showed anything. "Your last trip" below
       comes from the customer's own enquiries instead. */

    // Auto-fill from recent searches most frequent origin
    if (recentSearches && recentSearches.length > 0 && !localStorage.getItem("sramika_selected_city")) {
      const counts = {};
      let maxCount = 0;
      let mostFreqOrigin = recentSearches[0].from;

      recentSearches.forEach(s => {
        const loc = s.from.value || s.from;
        counts[loc] = (counts[loc] || 0) + (s.tripCount || 1);
        if (counts[loc] > maxCount) {
          maxCount = counts[loc];
          mostFreqOrigin = s.from;
        }
      });

      if (!fromLocation) {
        setFromLocation(mostFreqOrigin);
      }
    }

    /* The home page used to ask for location here, on first load, before the visitor
       had done anything or been told why. It did nothing with the answer either —
       both callbacks only logged.

       Asking that early costs more than it looks. Most people decline a permission
       they did not ask for, and Chrome remembers a decline as permanent: the next
       time location is genuinely needed — Detect Location, the route picker, the
       city chooser — the prompt never appears again, and the feature simply fails
       with no way for the app to re-ask. It also leaves a blocked-permission icon in
       the address bar in place of the padlock, which reads to a visitor as "this site
       is not secure" when nothing is wrong with it.

       So it is asked where it is needed and where the visitor has just asked for it:
       Header's "Use my location", LocationPicker and HireLocationPicker, the hire
       driver list, and the onboarding address field. Every one of those calls
       getCurrentPosition synchronously inside the click, which is what makes the
       native prompt appear at all. */

    track('home_viewed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* A first visit: store the default city, so the Hire Driver page - which reads the
     same key - agrees. From is deliberately left empty. Filling it with the city's
     name put the centre of Vijayawada into the location picker as if it were the
     customer's pickup; a pickup is a place the customer chooses, not a default.

     The second half clears that city-centre From for anyone who received it before
     this was corrected: exactly the default text, no coordinates, which no real
     pickup chosen in the picker ever looks like. */
  useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY)) localStorage.setItem(STORAGE_KEY, DEFAULT_CITY);
    const f = fromLocation;
    if (isCityCentreFrom(f)) {
      setFromLocation("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function applyCity(cityName, isManual = false) {
    setSelectedCity(cityName);
    localStorage.setItem(STORAGE_KEY, cityName);
    if (isManual) {
      sessionStorage.setItem("sramika_city_manually_set", "true");
    }
    /* Changing the city no longer writes the city centre into From - the same rule as
       the first visit. The list is chosen by the city itself (cityFilter), so From is
       not needed for it. A pickup the customer really chose is kept when it lies in
       the new city, and cleared when it does not, so a Guntur pickup cannot survive
       a switch to Vijayawada. */
    setFromLocation((prev) => {
      const text = typeof prev === "string" ? prev : (prev?.value || "");
      if (!text || isCityCentreFrom(prev)) return "";
      return text.toLowerCase().includes(String(cityName).toLowerCase()) ? prev : "";
    });
  }

  /* ⏱ Trip duration */
  useEffect(() => {
    let baseHours = 0;

    if (tripType === "ONE_WAY") {
      // For one-way, duration is strictly the drive time to destination
      baseHours = driveDurationMins / 60;
    } else {
      // Round/Local
      const end = returnDateTime || toDateTime;
      baseHours = fromDateTime && end ? calculateHours(fromDateTime, end) : 0;

      // Auto-calculate the estimated return journey time using Google Maps
      if (tripType === "ROUND" && driveDurationMins > 0) {
        baseHours += driveDurationMins / 60;
      }
    }

    setHours(baseHours);
  }, [fromDateTime, toDateTime, returnDateTime, tripType, driveDurationMins]);

  /*  Distance & Time */
  useEffect(() => {
    let active = true;
    setDistanceKm(0);
    setDriveDurationMins(0);
    const val = (loc) => loc?.value || loc || "";
    const from = val(fromLocation);
    const to   = val(toLocation);
    const legs = [from, ...(stops || []).map(val).filter(Boolean), to].filter(Boolean);

    async function calc() {
      if (legs.length < 2) {
        if (active) { setDistanceKm(0); setDriveDurationMins(0); }
        return;
      }

      // Reuse LocationPicker calculations directly if there are no intermediate stops
      if (location.state?.driveDurationMins && location.state?.tripDistanceKm && legs.length === 2) {
        if (active) {
          setDistanceKm(location.state.tripDistanceKm);
          setDriveDurationMins(location.state.driveDurationMins);
        }
        return;
      }

      let totDist = 0;
      let totTime = 0;
      for (let i = 0; i < legs.length - 1; i++) {
        const res = await calculateTripMetrics(legs[i], legs[i + 1]);
        totDist += res.distanceKm || 0;
        totTime += res.durationMinutes || 0;
      }
      if (active) {
        setDistanceKm(totDist);
        setDriveDurationMins(totTime);
      }
    }
    calc().catch(() => {
      if (active) { setDistanceKm(0); setDriveDurationMins(0); }
    });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromLocation, toLocation, stops]);

  /*  Auto return date */
  useEffect(() => {
    if (tripType === "ROUND" && toDateTime && !returnDateTime) setReturnDateTime(toDateTime);
    if (tripType === "ONE_WAY") setReturnDateTime("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripType, toDateTime, returnDateTime]);

  /* The trip in the address (mock Fix_A change 10): a link someone was sent fills the
     form once, on arrival; after that the address follows the form, so the page can be
     shared as it stands (utils/tripLink). */
  const linkRead = useRef(false);
  useEffect(() => {
    if (linkRead.current) return;
    linkRead.current = true;
    const q = readTripQuery(location.search);
    if (!q) return;
    if (q.from) setFromLocation(q.from);
    if (q.to) setToLocation(q.to);
    if (q.tripType) setTripType(q.tripType);
    if (q.on) setFromDateTime(q.on);
    if (q.back) setReturnDateTime(q.back);
    if (q.stops.length) setStops(q.stops);
    if (q.lang && q.lang !== lang) setLang(q.lang);
    track("trip_link_opened", { page: "cars" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!linkRead.current) return;
    syncTripUrl(tripQuery({ from: fromLocation, to: toLocation, tripType, on: fromDateTime, back: returnDateTime, stops, lang }));
    // location.key: the router replacing the address (navigate(".")) would drop the query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromLocation, toLocation, tripType, fromDateTime, returnDateTime, stops, lang, location.key]);
  const [linkNote, setLinkNote] = useState(false);
  const shareList = async () => {
    track("trip_link_shared", { page: "cars" });
    const how = await shareTripLink({ title: "Sramika" });
    if (how === "copied") { setLinkNote(true); setTimeout(() => setLinkNote(false), 2500); }
  };

  /*  Restore state from navigation */
  useEffect(() => {
    const state = location.state;
    if (!state) return;
    if (state.fromLocation) setFromLocation(state.fromLocation);
    if (state.toLocation) setToLocation(state.toLocation);
    if (state.fromDateTime) setFromDateTime(state.fromDateTime);
    if (state.toDateTime) setToDateTime(state.toDateTime);
    if (state.returnDateTime) setReturnDateTime(state.returnDateTime);
    if (state.tripType) setTripType(state.tripType);
    if (state.selectedCity !== undefined) setSelectedCity(state.selectedCity || "");
    if (state.stops) setStops(state.stops);
    if (state.showNoCabsPage) setNoCabsRequested(true);
    // A name found here from the Drivers search ("no driver by this name - found in Cars").
    if (typeof state.searchQuery === "string") setSearchQuery(state.searchQuery);
    navigate(".", { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, navigate]);

  const effectiveDistanceKm = (tripType === "ROUND" || tripType === "ROUND_TRIP") ? distanceKm * 2 : distanceKm;
  const fareContext = { hours, distanceKm: effectiveDistanceKm, tripType, cityPricing, calculateFare };

  /*  Role helpers */
  const effectiveUser = user || auth || null;
  const isLoggedIn = !!effectiveUser?.phone;
  const cityFilter = selectedCity;

  /* Login is NOT required just to browse — only enforce on car click or form submit */

  const handleCityChange = (nextCity) => {
    if (!nextCity) return;
    applyCity(nextCity, true);
  };

  /* ── The trip ─────────────────────────────────────────────────────────── */
  const fromText = placeText(fromLocation);
  const toText = placeText(toLocation);
  const routeIsSet = !!(fromText && toText);

  // The one way to set or change the trip: the trip form, handed what is set so far.
  // From a car's card the car goes along (returnToCar), and the form - and the sign-in
  // after it, if one is needed - returns to that car instead of this list.
  const openTripFor = (car) => navigate("/location-picker", {
    state: { tripType, selectedCity, fromLocation, toLocation, fromDateTime, toDateTime, returnDateTime, stops, ...(car?.carId ? { returnToCar: car } : {}) }
  });
  const openTripForm = () => openTripFor(null);

  /* A sentence understood by the search box (mock Four_1 change 7): the car asked for
     becomes the filters, and a place opens the trip form with it, the day and the time
     filled in - the customer adds the pickup there, as always. */
  const applyIntent = (intent) => {
    if (intent.seatsValue) setFilterSeats(intent.seatsValue);
    if (intent.carType) setFilterCarType(intent.carType);
    if (intent.fuel) setFilterFuelType(intent.fuel);
    if (intent.language) setFilterLanguage(intent.language);
    const drop = intentDrop(intent);
    if (drop) {
      navigate("/location-picker", { state: {
        tripType: intent.tripType || tripType, selectedCity, fromLocation, toLocation: drop,
        fromDateTime: intent.when || fromDateTime, toDateTime, returnDateTime: "",
        stops: (intent.trip?.stops || []).map((x) => ({ value: x.v, selected: true })),
      } });
    } else if (intent.when) {
      setFromDateTime(intent.when);
      if (intent.tripType) setTripType(intent.tripType);
    }
  };

  /* Swap opens the same form with pickup and drop changed round, rather than swapping
     them here: the form is what checks that a pickup lies within the city's reach, and
     a drop in another town must not become a pickup nobody here can reach. */
  const swapTrip = () => navigate("/location-picker", {
    state: {
      tripType, selectedCity, fromLocation: toLocation, toLocation: fromLocation,
      fromDateTime, toDateTime, returnDateTime, stops: [...(stops || [])].reverse(),
    }
  });

  // A recent route: set straight away, leaving two hours from now as the pickup time.
  const pickRecentRoute = (search) => {
    setFromLocation(search.from);
    setToLocation(search.to);
    if (search.seatType) setFilterSeats(search.seatType);
    if (search.category) setFilterCarType(search.category);
    const d = new Date();
    d.setHours(d.getHours() + 2);
    setFromDateTime(new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
    addRecentSearch(search);
    track('recent_route_tapped', { from: search.from, to: search.to });
  };

  const tripMeta = [
    tripWhen(fromDateTime, t, lang),
    t(tripType === "ONE_WAY" ? "lp_tabOneWay" : "lp_tabRoundTrip"),
    effectiveDistanceKm > 0 ? fillText(t("hm_km"), { km: Math.round(effectiveDistanceKm) }) : "",
  ].filter(Boolean).join(" · ");

  /* ── The customer's own enquiries: "did you book it?", then "your recent enquiry",
     shown once (see recentEnquiryOf) ── */
  const { leads: myLeads, pending: pendingAnswers } = useMyRecentLeads(isLoggedIn);
  const [hiddenEnquiries, setHiddenEnquiries] = useState(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const recentEnquiry = useMemo(() => recentEnquiryOf(myLeads, false), [myLeads, hiddenEnquiries]);

  /* ── Chips and the Filters sheet ──────────────────────────────────────── */
  const hasPickup = !!fromText;
  const opt = (o) => ({ value: o.value, label: optionLabel(o, t, lang) });

  // Seat sizes are the ones the listed cars have, so a 17-seater is there the day it
  // is listed and no size is offered that would show nothing.
  const seatOptions = useMemo(() => {
    const counts = new Set();
    allCars.forEach((c) => {
      const n = Number(c?.carDetails?.seats ?? c?.seatCount ?? c?.seats);
      if (Number.isFinite(n) && n > 0) counts.add(n);
    });
    return [...counts].sort((a, b) => a - b).map((n) => ({ value: seatsValue(n), key: "hm_nSeats", vars: { n } }));
  }, [allCars]);

  /* Every order is offered, always (the user, 26 Oct 2026: "Nearest first" had gone
     missing). One that needs a pickup or a trip not set yet opens the trip form, and
     the list is in that order once it is set. */
  const sortOptions = CAR_SORTS.map((o) => ({ value: o.value, label: t(o.key) }));
  const pickSort = (v) => {
    setSortBy(v);
    const needs = CAR_SORTS.find((o) => o.value === v)?.needs;
    if (needs && !(needs === "route" ? routeIsSet : hasPickup)) openTripForm();
  };
  const sortName = t((CAR_SORTS.find((o) => o.value === sortBy) || CAR_SORTS[0]).key);

  // What a chip says: the choice, or the first of several ("7 seats +1"), or its name.
  const chipText = (value, options, name) => {
    const vals = splitValues(value);
    if (!vals.length) return name;
    const first = options.find((o) => o.value === vals[0]);
    const text = first ? optionLabel(first, t, lang) : (customBudgetText(vals[0]) || vals[0]);
    return vals.length > 1 ? `${text} +${vals.length - 1}` : text;
  };
  const withAny = (options) => [{ value: "", label: t("hm_any") }, ...options.map(opt)];
  // A price needs a distance; until the route has one, the price choices wait.
  const priceReady = routeIsSet && effectiveDistanceKm > 0;

  const sheetGroups = [
    { title: t("hm_grpCar"), rows: [
      { key: "seats", label: t("hm_seats"), options: seatOptions.map(opt) },
      { key: "carType", label: t("hm_carType"), options: CAR_TYPES.map(opt) },
      { key: "fuelType", label: t("hm_fuel"), options: FUELS.map(opt) },
      { key: "year", label: t("hm_year"), options: YEARS.map(opt) },
    ] },
    { title: t("hm_grpDriver"), rows: [
      { key: "rating", label: t("hm_rating"), options: RATINGS.map(opt) },
      { key: "experience", label: t("hm_experience"), options: EXPERIENCE.map(opt) },
      { key: "language", label: t("hm_speaks"), options: CAR_LANGS.map(opt) },
      { key: "driverTags", label: t("hm_goodAt"), options: TAGS.map(opt) },
      { key: "driverAge", label: t("hm_driverAge"), options: AGES.map(opt) },
    ] },
    { title: t("hm_grpPrice"), rows: [
      { key: "budgetRange", options: BUDGETS.map(opt), disabled: !priceReady, note: priceReady ? null : t("hm_priceNeedsTrip") },
    ] },
  ];

  const countFor = (v) => allCars.filter((c) =>
    matchesDriverAge(c, v.driverAge) &&
    matchesCarType(c, v.carType) &&
    matchesSeats(c, v.seats) &&
    matchesYear(c, v.year) &&
    matchesFuelType(c, v.fuelType) &&
    matchesExperience(c, v.experience) &&
    matchesRating(c, v.rating) &&
    matchesDriverTags(c, v.driverTags) &&
    matchesBudgetRange(c, v.budgetRange, fareContext) &&
    matchesLanguage(c, v.language)
  ).length;

  return (
    <>
      <FilterSheet
        open={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        title={t("hm_filters")}
        groups={sheetGroups}
        values={filterValues}
        countFor={countFor}
        showLabel={(n) => (n === 0 ? t("hm_showNoCars") : n === 1 ? t("hm_showOneCar") : fillText(t("hm_showCars"), { n }))}
        clearLabel={t("hm_clearAll")}
        closeLabel={t("hm_close")}
        onApply={(next) => {
          FILTER_KEYS.forEach((k) => filters[k][1](next[k] || ""));
          setShowFilterModal(false);
        }}
      />
      {showExplainer && <FirstLaunchExplainer onComplete={handleExplainerComplete} />}
      {/* Bottom space sized to the nav: its 58px, the phone's home-indicator inset, and
          a 12px gap - enough for the last card to clear the bar, and no more. */}
      <div className="page-container hm lg:!max-w-[1240px] lg:!px-6" style={{ paddingBottom: "calc(70px + env(safe-area-inset-bottom, 0px))" }}>
        <Header city={selectedCity} onCityChange={handleCityChange} onLocate={setFromLocation} />

        {/* ── A laptop (mock Laptop_1): a headline, the trip as one wide bar, the trips
            card beside it - the phone's trip card, banners and chips stay on phones ── */}
        <DesktopHero
          title={fillText(t("dk_heroCars"), { city: localCity(selectedCity || "Vijayawada", lang) })}
          sub={t("dk_subCars")}
          below={<><TrustRow items={[["shield", t("dk_trustCars")], ["rupee", t("dk_trustPrice")], ["phone", t("dk_trustCallOwner")]]} /><CallToBookLine t={t} from="home_laptop" /></>}
          search={(
            <DesktopSearchBar onOpen={openTripForm} cta={t("dk_searchCars")}
              onCta={() => (routeIsSet ? document.getElementById("cars-list")?.scrollIntoView({ behavior: "smooth", block: "start" }) : openTripForm())}
              parts={[
                { kind: "pickup", label: t("lp_lblPickup"), value: shortPlace(fromLocation), placeholder: t("lp_phPrecisePickup") },
                { kind: "drop", label: t("lp_lblDrop"), value: shortPlace(toLocation), placeholder: t("lp_phDropLocation") },
                { icon: TripIcons.calendar(14), label: t("dk_when"), value: tripWhen(fromDateTime, t, lang), placeholder: t("dk_pickWhen") },
                { icon: TripIcons.swap(14), label: t("dk_tripType"), value: t(tripType === "ONE_WAY" ? "lp_tabOneWay" : "lp_tabRoundTrip"), narrow: true },
              ]} />
          )}
          aside={selectedCity === SEASON_CITY ? (
            <SeasonTrips asCard cars={allCars} cityPricing={cityPricing} calculateFare={calculateFare} lang={lang}
              onOpenIndex={() => { track("trips_index_link", { from: "home_card" }); navigate("/trips"); }}
              onOpen={(trip) => { track("season_trip_tapped", { trip: trip.slug, from: "card" }); navigate(`/trips/${trip.slug}`); }} />
          ) : null} />

        <div className="lg:flex lg:items-start lg:gap-6 lg:mt-6">
        {/* The filters, always open beside the list (a laptop). */}
        <aside className="hidden lg:block w-[270px] shrink-0 sticky top-[80px]">
          <FilterPanel title={t("dk_filters")} clearLabel={t("hm_clearAll")} groups={sheetGroups} values={filterValues}
            sort={{ label: t("hm_sort"), value: sortBy, onChange: pickSort, options: sortOptions }}
            onToggle={(key, value) => filters[key][1](toggled(filters[key][0], value))}
            onClear={clearAllFilters} />
        </aside>
        <div className="flex-1 min-w-0">
        {/* A phone: the same order and spacing as the Drivers page (the user, 24 Oct
            2026) - the visitor's own enquiry, the trip card and the call line, the
            trips, the search, the chips, then the count line and the cars, 8px apart.
            The login card for visitors is under the third car. */}
        <div className="mt-2 space-y-2 lg:mt-0">
          {/* ── Signed in: the one question the funnel never asked, and otherwise the
              most recent enquiry, asking for its stars and a line of feedback - shown
              on one visit only ──────────────────────────────────────────────── */}
          {isLoggedIn && (pendingAnswers[0]
            ? <LeadOutcomePrompt lead={pendingAnswers[0]} />
            : recentEnquiry && (
              <RecentEnquiryCard lead={recentEnquiry} t={t}
                onHide={(lead) => { hideEnquiry(lead); setHiddenEnquiries((n) => n + 1); }} />
            ))}

          {/* ── The trip: where, when - one card, one form ─────────────────── */}
          <div className="lg:hidden"><WhereCard
            t={t}
            from={shortPlace(fromLocation)}
            to={shortPlace(toLocation)}
            meta={routeIsSet ? tripMeta : ""}
            onOpen={openTripForm}
            onSwap={swapTrip}
            recent={routeIsSet ? [] : recentSearches.slice(0, 3).map((s, i) => ({
              key: i,
              label: `${shortPlace(s.from)} → ${shortPlace(s.to)}`,
              onClick: () => pickRecentRoute(s),
            }))}
          />
          {/* Call to book (mock CallBook_1), one quiet line since mock Home_1. */}
          <CallToBookBar t={t} from="home" /></div>

          {/* ── The season's trips from Vijayawada, "from ₹" for everyone (Oct 2026);
              each opens its own page ─────────────────────────────────────── */}
          {selectedCity === SEASON_CITY && (
            <div className="lg:hidden pt-1"><SeasonTrips cars={allCars} cityPricing={cityPricing} calculateFare={calculateFare} lang={lang}
              onOpenIndex={() => { track("trips_index_link", { from: "home_row" }); navigate("/trips"); }}
              onOpen={(trip) => { track("season_trip_tapped", { trip: trip.slug }); navigate(`/trips/${trip.slug}`); }} /></div>
          )}

          {/* ── Search, and the four filters people reach for; the rest are in
              Filters, whose button counts every one that is on ────────────────── */}
          <SearchBar
            onSearch={setSearchQuery}
            initialValue={searchQuery}
            onFilterClick={() => setShowFilterModal(true)}
            placeholder={t("sq_phCars")}
            filterCount={activeFilterCount}
            page="cars"
            onIntent={applyIntent}
          />

          <div className="lg:hidden"><ChipRow>
            {/* Always showing its current value, because unlike a filter there is no
                "off" state - something is always deciding the order. */}
            <QuickChip
              icon={TripIcons.swap(14)}
              label={sortBy === "RECOMMENDED" ? t("hm_sort") : sortName}
              active={sortBy !== "RECOMMENDED"}
              value={sortBy}
              options={sortOptions}
              onChange={pickSort}
              pickLabel={t("hm_sort")}
            />
            <QuickChip
              icon={<Icon name="users" size={14} strokeWidth={2.2} />}
              label={chipText(filterSeats, seatOptions, t("hm_seats"))}
              active={!!filterSeats}
              value={splitValues(filterSeats)[0] || ""}
              options={withAny(seatOptions)}
              onChange={setFilterSeats}
              onClear={() => setFilterSeats("")}
              clearLabel={t("hm_removeFilter")}
              pickLabel={t("hm_seats")}
            />
            <QuickChip
              icon={<Icon name="car" size={14} strokeWidth={2.2} />}
              label={chipText(filterCarType, CAR_TYPES, t("hm_carType"))}
              active={!!filterCarType}
              value={splitValues(filterCarType)[0] || ""}
              options={withAny(CAR_TYPES)}
              onChange={setFilterCarType}
              onClear={() => setFilterCarType("")}
              clearLabel={t("hm_removeFilter")}
              pickLabel={t("hm_carType")}
            />
            {/* A price needs the trip. Until there is one, Price opens the trip form
                instead of offering ranges that would empty the list. */}
            <QuickChip
              icon={<Icon name="rupee" size={14} strokeWidth={2.2} />}
              label={chipText(filterBudgetRange, BUDGETS, t("hm_price"))}
              active={!!filterBudgetRange}
              value={splitValues(filterBudgetRange)[0] || ""}
              options={priceReady ? withAny(BUDGETS) : null}
              onChange={setFilterBudgetRange}
              onClick={priceReady ? null : openTripForm}
              onClear={() => setFilterBudgetRange("")}
              clearLabel={t("hm_removeFilter")}
              pickLabel={t("hm_price")}
            />
            <QuickChip
              icon={<Icon name="user" size={14} strokeWidth={2.2} />}
              label={chipText(filterDriverAge, AGES, t("hm_driverAge"))}
              active={!!filterDriverAge}
              value={splitValues(filterDriverAge)[0] || ""}
              options={withAny(AGES)}
              onChange={setFilterDriverAge}
              onClear={() => setFilterDriverAge("")}
              clearLabel={t("hm_removeFilter")}
              pickLabel={t("hm_driverAge")}
            />
          </ChipRow></div>
        </div>

        {/* ── The cars ───────────────────────────────────────────────────── */}
        <div id="cars-list" className="mt-2 scroll-mt-[84px]">
          <CarGrid
            tripType={tripType}
            hours={hours}
            distanceKm={effectiveDistanceKm}
            fromLocation={fromLocation}
            toLocation={toLocation}
            fromDateTime={fromDateTime}
            returnDateTime={returnDateTime}
            stops={stops}
            calculateFare={calculateFare}
            cityPricing={cityPricing}
            cityFilter={cityFilter}
            searchQuery={listSearch}
            onUsePlaceAsPickup={(place) => {
              /* A place found by the search bar becomes the pickup, shaped like one chosen
                 in the picker - text plus coordinates - so the list re-ranks by real
                 distance from it and the cards show arrival times. */
              setFromLocation({ value: place.address, location: { lat: place.lat, lng: place.lng }, selected: true });
              track("search_place_as_pickup", { place: place.name });
            }}
            onUsePlaceAsDrop={(place) => {
              /* Another city typed into the search: made the drop, shaped like a place
                 chosen in the picker. Coordinates when the map answered, text otherwise. */
              setToLocation(place.lat != null
                ? { value: place.address, location: { lat: place.lat, lng: place.lng }, selected: true }
                : { value: place.address, selected: true });
              track("search_city_as_drop", { city: place.name });
            }}
            onClearFilters={clearAllFilters}
            filterDriverAge={filterDriverAge}
            filterCarType={filterCarType}
            filterSeats={filterSeats}
            filterYear={filterYear}
            filterFuelType={filterFuelType}
            filterExperience={filterExperience}
            filterRating={filterRating}
            filterDriverTags={filterDriverTags}
            filterBudgetRange={filterBudgetRange}
            filterLanguage={filterLanguage}
            sortBy={sortBy}
            sortLabel={sortName}
            onSetRoute={openTripFor}
            onCarsLoaded={setAllCars}
            onShare={shareList}
            afterThird={isLoggedIn ? null : (
              <GuestCard title={t("hm_guestCardT")} sub={t("hm_guestCardD")}
                cta={t("hm_loginShort")} onLogin={() => navigate("/login")} />
            )}
            onNoCars={(city) => {
              if (!noCabsRequested) return;
              setNoCabsRequested(false);
              navigate("/no-cabs", { state: { city } });
            }}
            /* Prices and a car's page are open to everyone; only Call asks for a login
               (19 Oct 2026, mock Trips_8). */
            showPriceBreakup
            bookingEnabled={isLoggedIn}
          />
        </div>
        </div>
        </div>
      </div>
      <DesktopFooter t={t} />

      {linkNote && (
        <div role="status" className="fixed left-1/2 -translate-x-1/2 bottom-[calc(84px+env(safe-area-inset-bottom,0px))] z-50 rounded-full bg-[#0F172A] text-white text-[11.5px] font-bold px-4 py-2 shadow-lg">
          {t("hm_linkCopied")}
        </div>
      )}
      <BottomNav />
    </>
  );
}
